import { ChangeDetectionStrategy, Component, computed, inject, input } from "@angular/core";
import { QueryStats } from "../models/Metric";
import { Metric, MetricType, PALETTE } from "@front/m19-metrics";
import { Currency, QueryType } from "@front/m19-api-client";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ChartConfig, IChartComponent, SpinnerComponent } from "@front/m19-ui";
import { sumAdStats } from "@front/m19-utils";
import { GroupByFn } from "@front/m19-models/dataset/Dataset";

interface TrafficColors {
  total: string;
  kw: string;
  targetedProduct: string;
  other: string;
}

export const TRAFFIC_STATS_COLORS: { segment: TrafficColors; nonSegment: TrafficColors } = {
  segment: {
    total: PALETTE[0],
    kw: PALETTE[1],
    targetedProduct: PALETTE[2],
    other: PALETTE[4],
  },
  nonSegment: {
    total: PALETTE[3],
    kw: "#81C6B8",
    targetedProduct: "#F5C683",
    other: "#b6b6b6",
  },
};

type BarCategory = "total" | "kw" | "product" | "other";
type BarChartRow = QueryStats & { barCategory: BarCategory };

const BAR_CATEGORY_ORDER: Record<BarCategory, number> = { total: 0, kw: 1, product: 2, other: 3 };

@Component({
  selector: "app-traffic-stats-chart",
  templateUrl: "./traffic-stats-chart.component.html",
  styleUrls: ["./traffic-stats-chart.component.scss"],
  imports: [TranslocoDirective, SpinnerComponent, IChartComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrafficStatsChartComponent {
  private translocoService = inject(TranslocoService);

  readonly RATIO = MetricType.RATIO;

  loader = input.required<boolean>();
  data = input.required<QueryStats[]>();
  metric = input.required<Metric<QueryStats>>();
  currency = input.required<Currency>();
  locale = input.required<string>();

  donutChartConfig = computed<ChartConfig<QueryStats, "doughnut">>(() => {
    const outerRingGroupBy: GroupByFn<QueryStats> = (s) => {
      const bucket = s.segmentBucket ?? "segment";
      return {
        key: bucket,
        label:
          bucket === "segment"
            ? this.translocoService.translate("traffic-stats-chart.filtered_traffic")
            : this.translocoService.translate("traffic-stats-chart.rest_of_traffic"),
        color: TRAFFIC_STATS_COLORS[bucket].total,
      };
    };

    const innerRingGroupBy: GroupByFn<QueryStats> = (s) => {
      const bucket = s.segmentBucket ?? "segment";
      const palette = TRAFFIC_STATS_COLORS[bucket];
      if (s.queryType === QueryType.KEYWORDS) {
        return {
          key: `${bucket}-kw`,
          label: this.translocoService.translate("traffic-stats-chart.kw"),
          color: palette.kw,
        };
      }
      if (s.queryType === QueryType.PRODUCT) {
        return {
          key: `${bucket}-product`,
          label: this.translocoService.translate("traffic-stats.targeted_product"),
          color: palette.targetedProduct,
        };
      }
      return {
        key: `${bucket}-other`,
        label: this.translocoService.translate("dsp-stats.dspCampaignDeliveryStatus_OTHER"),
        color: palette.other,
      };
    };

    return {
      type: "doughnut",
      isDoubleDonut: true,
      aggFunctions: [(a: QueryStats, b: QueryStats) => sumAdStats(a, b) as QueryStats],
      metrics: [this.metric()],
      groupByFunctions: [outerRingGroupBy, innerRingGroupBy],
    };
  });

  barChartRows = computed<BarChartRow[]>(() => {
    const segmentRows = this.data().filter((s) => s.segmentBucket === "segment");
    return this.getBarChartRow(segmentRows);
  });

  restOfTrafficBarChartRows = computed<BarChartRow[]>(() => {
    const segmentRows = this.data().filter((s) => s.segmentBucket === "nonSegment");

    return this.getBarChartRow(segmentRows);
  });

  filteredDataBarChartConfig = computed<ChartConfig<BarChartRow, "bar">>(() => {
    return this.buildBarChartConfig(TRAFFIC_STATS_COLORS.segment);
  });

  restOfTrafficBarChartConfig = computed<ChartConfig<BarChartRow, "bar">>(() => {
    return this.buildBarChartConfig(TRAFFIC_STATS_COLORS.nonSegment);
  });

  private getBarChartRow(segmentRows: QueryStats[]): BarChartRow[] {
    if (segmentRows.length === 0) return [];

    const kwRows = segmentRows.filter((s) => s.queryType === QueryType.KEYWORDS);
    const productRows = segmentRows.filter((s) => s.queryType === QueryType.PRODUCT);
    const otherRows = segmentRows.filter(
      (s) => s.queryType !== QueryType.KEYWORDS && s.queryType !== QueryType.PRODUCT,
    );

    const result: BarChartRow[] = [{ ...this.sumRows(segmentRows), barCategory: "total" }];
    if (kwRows.length > 0) result.push({ ...this.sumRows(kwRows), barCategory: "kw" });
    if (productRows.length > 0) result.push({ ...this.sumRows(productRows), barCategory: "product" });
    if (otherRows.length > 0) result.push({ ...this.sumRows(otherRows), barCategory: "other" });
    return result;
  }

  private buildBarChartConfig(colors: TrafficColors): ChartConfig<BarChartRow, "bar"> {
    const groupByFn: GroupByFn<BarChartRow> = (r) => {
      switch (r.barCategory) {
        case "total":
          return {
            key: this.translocoService.translate("common.total"),
            label: this.translocoService.translate("common.total"),
            color: colors.total,
          };
        case "kw":
          return {
            key: this.translocoService.translate("traffic-stats-chart.kw"),
            label: this.translocoService.translate("traffic-stats-chart.kw"),
            color: colors.kw,
          };
        case "product":
          return {
            key: this.translocoService.translate("traffic-stats.targeted_product"),
            label: this.translocoService.translate("traffic-stats.targeted_product"),
            color: colors.targetedProduct,
          };
        case "other":
          return {
            key: this.translocoService.translate("dsp-stats.dspCampaignDeliveryStatus_OTHER"),
            label: this.translocoService.translate("dsp-stats.dspCampaignDeliveryStatus_OTHER"),
            color: colors.other,
          };
      }
    };

    return {
      type: "bar",
      indexAxis: "y",
      aggFunctions: [(a: BarChartRow, b: BarChartRow) => sumAdStats(a, b) as BarChartRow],
      metrics: [this.metric() as Metric<BarChartRow>],
      groupByFunctions: [groupByFn],
      sortBy: (a, b) => BAR_CATEGORY_ORDER[a.barCategory] - BAR_CATEGORY_ORDER[b.barCategory],
    };
  }

  private sumRows(rows: QueryStats[]): QueryStats {
    return rows.reduce((acc, current) => ({ ...current, ...sumAdStats(acc, current) }), {} as QueryStats);
  }
}
